import { Control, Controller } from "react-hook-form";
import { useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/shadcn/button";
import { NoxusInput } from "@/components/Form/InputField";
import { findMaxStringValue } from "@/utils/generic";
import { Plus, Trash } from "lucide-react";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { cn } from "@/lib/utils";
import TiptapEditor from "./TiptapEditor/TiptapEditor";
import { RichTextValue, getTemplateStringValue } from "./RichTextField";
import { type AdvancedChipOption } from "./TiptapEditor/chipOptionsConfig";
import { v4 as uuidv4 } from "uuid";
import { NoxusFormLabel } from "./NoxusFormLabel";

type CriterionEntry = Record<string, string | RichTextValue>;

interface ICriteriaListInputProps {
  title?: string;
  entries: CriterionEntry[];
  onChange: (value: CriterionEntry[]) => void;
  keyLabel: string;
  valueLabel: string;
  keyPrefix: string;
  valuePlaceholder: string;
  isDisabled?: boolean;
  chipOptions?: AdvancedChipOption[];
  helperText?: string;
}

interface EntryWithId {
  id: string;
  data: CriterionEntry;
}

const parseRichText = (
  val: string | RichTextValue | undefined
): RichTextValue => {
  if (!val) return { text: "", definition: {}, isRaw: false };
  if (typeof val === "string") return getTemplateStringValue(val);
  if (val.definition) return val;
  return getTemplateStringValue(String(val));
};

const CriteriaListInput = (props: ICriteriaListInputProps) => {
  const {
    entries,
    keyLabel,
    valueLabel,
    keyPrefix,
    valuePlaceholder,
    isDisabled,
    chipOptions,
    helperText,
  } = props;

  const listRef = useRef<EntryWithId[]>(
    entries.map((v) => ({ id: uuidv4(), data: v }))
  );

  // Sync ref when external length changes (e.g. form reset)
  if (listRef.current.length !== entries.length) {
    listRef.current = entries.map((v) => ({ id: uuidv4(), data: v }));
  }

  const cantDelete = useMemo(
    () => entries.length <= 1 || !!isDisabled,
    [entries, isDisabled]
  );

  const emit = () => props.onChange(listRef.current.map((e) => e.data));

  const handleAddClick = useCallback(() => {
    if (isDisabled) return;
    const maxVal = findMaxStringValue(
      listRef.current.map((e) => String(e.data[keyLabel] ?? "")),
      new RegExp(`^${keyPrefix} (\\d+)$`)
    );
    listRef.current = [
      ...listRef.current,
      {
        id: uuidv4(),
        data: {
          [keyLabel]: `${keyPrefix} ${maxVal + 1}`,
          [valueLabel]: "",
        },
      },
    ];
    emit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyPrefix, keyLabel, valueLabel, isDisabled]);

  const handleDeleteClick = useCallback(
    (index: number) => {
      if (cantDelete || isDisabled) return;
      listRef.current = listRef.current.filter((_, i) => i !== index);
      emit();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cantDelete, isDisabled]
  );

  const handleKeyChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    if (isDisabled) return;
    listRef.current = listRef.current.map((entry, i) =>
      i === index
        ? { ...entry, data: { ...entry.data, [keyLabel]: event.target.value } }
        : entry
    );
    emit();
  };

  const handleValueChange = (id: string, newValue: RichTextValue) => {
    if (isDisabled) return;
    listRef.current = listRef.current.map((entry) =>
      entry.id === id
        ? { ...entry, data: { ...entry.data, [valueLabel]: newValue } }
        : entry
    );
    emit();
  };

  return (
    <>
      {props.title && <NoxusFormLabel label={props.title} />}
      {helperText && (
        <p className="text-xs text-muted-foreground mb-3 leading-normal">
          {helperText}
        </p>
      )}
      <div className="gap-6 flex flex-col items-start">
        {listRef.current.map((entry, index) => (
          <div
            key={entry.id}
            className="w-full gap-4 flex flex-col items-end relative border border-border rounded-md p-4"
          >
            <NoxusInput
              value={String(entry.data[keyLabel] ?? "")}
              label={keyLabel}
              forcedName={"Category required"}
              onChange={(e) => handleKeyChange(e, index)}
              placeholder={keyPrefix}
              disable={isDisabled}
            />

            <div className="w-full" onPointerDown={(e) => e.stopPropagation()}>
              <label className="text-sm font-medium text-primary mb-1.5 block">
                {valueLabel}
              </label>
              <TiptapEditor
                onChange={(val: RichTextValue) =>
                  handleValueChange(entry.id, val)
                }
                value={parseRichText(entry.data[valueLabel])}
                chipOptions={chipOptions}
                placeholder={valuePlaceholder}
                config={{
                  hideChipOptions: true,
                  hideToolbar: true,
                  numberLines: 3,
                }}
                isDisabled={isDisabled}
              />
              <span className="text-xs font-regular leading-normal text-muted-foreground whitespace-pre-line mt-1">
                Insert{" "}
                <span className="font-semibold text-foreground bg-muted rounded-sm px-1.5">
                  /
                </span>{" "}
                to see available inputs.
              </span>
            </div>

            <div className="absolute -top-3 -right-3">
              <NoxusTooltip
                placement="left"
                content={cantDelete ? "Can't have less than 1 value" : ""}
                asChild
              >
                <Button
                  variant="outline"
                  size="iconsm"
                  className="rounded-full disabled:opacity-100"
                  isDisabled={cantDelete || isDisabled}
                  onClick={() => handleDeleteClick(index)}
                >
                  <Trash
                    className={cn(
                      "h-2 w-2",
                      (cantDelete || isDisabled) && "opacity-25"
                    )}
                    strokeWidth="1.33px"
                  />
                </Button>
              </NoxusTooltip>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={handleAddClick}
          className="mx-auto mb-4"
          isDisabled={isDisabled}
        >
          <Plus size="1.25rem" strokeWidth="1.33px" />
          {`Add ${props.keyPrefix?.toLowerCase() || "more"}`}
        </Button>
      </div>
    </>
  );
};

interface ICriteriaListFieldProps {
  name: string;
  tooltip?: string;
  label?: string;
  control: Control<any>;
  defaultValue?: Record<string, string>[];
  keyLabel: string;
  valueLabel: string;
  keyPrefix: string;
  valuePlaceholder: string;
  isDisabled?: boolean;
  chipOptions?: AdvancedChipOption[];
  helperText?: string;
}

export const CriteriaListField = (props: ICriteriaListFieldProps) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value } }) => {
        const entries: CriterionEntry[] =
          Array.isArray(value) && value.length > 0
            ? value
            : (props.defaultValue ?? []);

        return (
          <CriteriaListInput
            entries={entries}
            title={props.label}
            onChange={onChange}
            keyLabel={props.keyLabel}
            valueLabel={props.valueLabel}
            keyPrefix={props.keyPrefix}
            valuePlaceholder={props.valuePlaceholder}
            isDisabled={props.isDisabled}
            chipOptions={props.chipOptions}
            helperText={props.helperText}
          />
        );
      }}
    />
  );
};
